import React from "react";
import {
	Button,
	ChildrenProps,
	ClassNameProps,
	Column,
	Conditional,
	Else,
	FormattedText,
	Grid,
	Heading,
	If,
	List,
	makeClassName,
	Modal,
	NullablePick,
	Row,
	Rule,
	Section,
	StyleProps,
	Timestamp,
	toGrammaticalNumber,
	Tooltip,
	TooltipButton,
} from "@hex-insights/core";
import { EditMode } from "@hex-insights/forms";
import { Icon } from "../../../../Components/Icon";
import { ViewRecorder } from "../../../../Components/ViewRecorder";
import {
	Student,
	StudentAcademicNote,
	StudentAttendanceNote,
	StudentBehaviorNote,
	StudentHealthNote,
	StudentIncidentNote,
	StudentInjuryNote,
	StudentMedicationNote,
	StudentNameAndImageFragment,
	StudentNote,
	StudentNoteNoteType,
	StudentNoteUpdateInput,
	StudentSocialEmotionalNote,
	User,
	UserNameAndImageFragment,
} from "../../../../Utilities";
import { StudentNoteDeleteButton } from "../../../DeleteButtons";
import {
	AttendanceStatusFormat,
	StudentAcademicNoteFormat,
	StudentAttendanceNoteFormat,
	StudentBehaviorNoteFormat,
	StudentFormat,
	StudentIncidentNoteFormat,
	StudentInjuryNoteFormat,
	StudentNoteFormat,
	StudentSocialEmotionalNoteFormat,
	UserFormat,
} from "../../../Format";
import { StudentNoteResolutionForm } from "../../../Forms";
import { StudentNoteFormValues } from "../../../FormUtilities";
import { StudentLink, UserLink } from "../../../Links";
import { StudentNoteUpdateButton } from "../../../UpdateButtons";
import { MediaAttachmentUtilities } from "../../../Utilities";
import { AttendanceStatusBadge } from "../../AttendanceStatus";
import * as MediaAttachmentComponents from "../../MediaAttachment";
import { SharingView } from "../../SharingRecord";
import * as SocialCommentComponents from "../../SocialComment";
import * as StudentComponents from "../../Student";
import * as UserComponents from "../../User";
import {
	StudentNoteDeletePermissionRequired,
	StudentNoteModifyPermissionRequired,
	StudentNoteUpdatePermissionRequired,
} from "../PermissionRequired";
import styles from "./styles.module.css";

export type DetailDisplayStudentNote = Pick<
	StudentNote,
	| "id"
	| "noteType"
	| "createdAt"
	| "body"
	| "isPinned"
	| "requiresResolution"
	| "isResolved"
	| "resolutionDescription"
	| "resolvedAt"
> & {
	author: (Pick<User, "id"> & UserNameAndImageFragment) | null;
	primaryStudents: (Pick<Student, "id"> & StudentNameAndImageFragment)[];
	studentAcademicNote: Pick<
		StudentAcademicNote,
		| "isIssueNatureAttentionIssue"
		| "isIssueNatureEmotionalOrVulnerableIssue"
		| "isIssueNatureFocusIssue"
		| "isIssueNatureLearningDifficulty"
		| "isIssueNatureOther"
		| "isSolutionRecDifferentiatedLearning"
		| "isSolutionRecInClassSupport"
		| "isSolutionRecIndividualLearningPlan"
		| "isSolutionRecOther"
		| "isSolutionRecProfessionalSupport"
		| "isSolutionRecTutoring"
		| "issueNatureOther"
		| "solutionRecOther"
	> | null;
	studentAttendanceNote:
		| (Pick<StudentAttendanceNote, "startDate" | "endDate"> & {
				attendanceStatus: NullablePick<
					StudentAttendanceNote["attendanceStatus"],
					| "id"
					| "name"
					| "code"
					| "description"
					| "indicatesAbsent"
					| "indicatesLateArrival"
					| "indicatesEarlyDeparture"
					| "indicatesExcused"
				>;
		  })
		| null;
	studentBehaviorNote: Pick<
		StudentBehaviorNote,
		| "additionalComments"
		| "consequenceRecOther"
		| "incidentLocation"
		| "incidentOther"
		| "incidentTime"
		| "isConsequenceRecDirectorIntervention"
		| "isConsequenceRecOther"
		| "isConsequenceRecParentContactFromReporter"
		| "isConsequenceRecParentContactFromSchoolDirector"
		| "isConsequenceRecSenOrSelIntervention"
		| "isConsequenceRecStudentServiceTeamIntervention"
		| "isIncidentDefianceOrNonCompliance"
		| "isIncidentDisrepectingTeacher"
		| "isIncidentDisruption"
		| "isIncidentFightingPhysicalAggression"
		| "isIncidentOther"
		| "isIncidentPhysicalContactAggression"
		| "isIncidentVerbalAbuseDisrespectingStudent"
		| "isMotivationAnger"
		| "isMotivationAttentionSeeking"
		| "isMotivationFrustration"
		| "isMotivationHabit"
		| "isMotivationLackOfSelfContol"
		| "isMotivationOther"
		| "isMotivationReaction"
		| "motivationOther"
	> | null;
	studentHealthNote: Pick<StudentHealthNote, "body"> | null;
	studentIncidentNote: Pick<
		StudentIncidentNote,
		"actionsTaken" | "incidentLocation" | "incidentTime" | "suggestionsForFuture"
	> | null;
	studentInjuryNote: Pick<
		StudentInjuryNote,
		| "actionTakenOther"
		| "adultWhoProvidedMedicalAttention"
		| "causeOfInjury"
		| "incidentLocation"
		| "incidentTime"
		| "isActionTakenHomeroomTeachersNotified"
		| "isActionTakenOther"
		| "isActionTakenParentNotificationNotNecessary"
		| "isActionTakenParentNotifiedByPhone"
		| "isActionTakenParentNotifiedByPortal"
		| "isActionTakenParentsRequestedToCollectStudent"
		| "medicalAttentionProvided"
		| "wereOtherStudentsCauseOfInjury"
	> | null;
	studentMedicationNote: Pick<StudentMedicationNote, "body"> | null;
	studentSocialEmotionalNote: Pick<
		StudentSocialEmotionalNote,
		| "isIssueNatureAttentionIssue"
		| "isIssueNatureEmotionalOrVulnerableIssue"
		| "isIssueNatureFocusIssue"
		| "isIssueNatureLearningDifficulty"
		| "isIssueNatureOther"
		| "isSolutionRecDifferentiatedLearning"
		| "isSolutionRecInClassSupport"
		| "isSolutionRecIndividualLearningPlan"
		| "isSolutionRecOther"
		| "isSolutionRecProfessionalSupport"
		| "isSolutionRecTutoring"
		| "issueNatureOther"
		| "solutionRecOther"
	> | null;
	mediaAttachments: Pick<
		StudentNote["mediaAttachments"][0],
		"id" | "title" | "description" | "isFile" | "fileURL" | "fileKey" | "fileContentType" | "linkURL"
	>[];
	socialComments: SocialCommentComponents.CommentInView[];
};

export type StudentNoteDetailDisplayProps = {
	isLoading?: boolean;
	studentNote: DetailDisplayStudentNote | null | undefined;
	onDelete?: () => void;
} & Partial<ClassNameProps & StyleProps>;

export function StudentNoteDetailDisplay({ isLoading, studentNote, className, style }: StudentNoteDetailDisplayProps) {
	if (isLoading && !studentNote) {
		return <div>Loading...</div>;
	}

	if (!studentNote) {
		return null;
	}

	return (
		<Column justify="spaced-start" verticalSpacing="1.5rem" className={className} style={style}>
			<ViewRecorder id={studentNote.id} />

			<Row justify="spaced-start" className={styles["detail-display__summary-information"]}>
				<Grid tracks={3} style={{ flexGrow: 1 }}>
					<LabeledItem label={StudentNoteFormat.authorLabel(studentNote)}>
						<Conditional>
							<If condition={!!studentNote.author}>
								{!!studentNote.author && (
									<UserLink instance={studentNote.author} className="link--no-text-decoration">
										<Row
											justify="spaced-start"
											align="center"
											horizontalSpacing="0.5rem"
											className={styles["detail-display__labeled-item__value--person"]}
										>
											<UserComponents.UserIcon user={studentNote.author} size={16} />
											{UserFormat.name(studentNote.author)}
										</Row>
									</UserLink>
								)}
							</If>
							<Else>
								<span>User Deleted</span>
							</Else>
						</Conditional>
					</LabeledItem>

					<LabeledItem label={toGrammaticalNumber("Student", studentNote.primaryStudents.length)}>
						<Column justify="spaced-start" verticalSpacing="0.25rem">
							{studentNote.primaryStudents.map((student) => (
								<StudentLink key={student.id} instance={student} className="link--no-text-decoration">
									<Row
										justify="spaced-start"
										align="center"
										horizontalSpacing="0.5rem"
										className={styles["detail-display__labeled-item__value--person"]}
									>
										<StudentComponents.StudentIcon student={student} size={16} />
										{StudentFormat.name(student)}
									</Row>
								</StudentLink>
							))}
						</Column>
					</LabeledItem>

					<LabeledItem label="Written">
						<span>{StudentNoteFormat.Fields.createdAt(studentNote.createdAt)}</span>
					</LabeledItem>
				</Grid>

				<StudentNoteModifyPermissionRequired
					isAuthor={studentNote.author?.isCurrent ?? false}
					noteType={studentNote.noteType}
				>
					<MutationMenu studentNote={studentNote} />
				</StudentNoteModifyPermissionRequired>
			</Row>

			<LabeledItem label={StudentNoteFormat.bodyLabel(studentNote)}>
				<FormattedText>{studentNote.body}</FormattedText>
			</LabeledItem>

			<If condition={studentNote.noteType !== StudentNoteNoteType.General}>
				<Rule thickness="2px" color="var(--neutral-colors--stroke)" />
			</If>

			<Conditional>
				<If
					condition={studentNote.noteType === StudentNoteNoteType.Academic && studentNote.studentAcademicNote !== null}
				>
					<AcademicDetails studentNote={studentNote} />
				</If>
				<If
					condition={
						studentNote.noteType === StudentNoteNoteType.Attendance && studentNote.studentAttendanceNote !== null
					}
				>
					<AttendanceDetails studentNote={studentNote} />
				</If>
				<If
					condition={studentNote.noteType === StudentNoteNoteType.Behavior && studentNote.studentBehaviorNote !== null}
				>
					<BehaviorDetails studentNote={studentNote} />
				</If>
				<If condition={studentNote.noteType === StudentNoteNoteType.Health && studentNote.studentHealthNote !== null}>
					<HealthDetails studentNote={studentNote} />
				</If>
				<If
					condition={studentNote.noteType === StudentNoteNoteType.Incident && studentNote.studentIncidentNote !== null}
				>
					<IncidentDetails studentNote={studentNote} />
				</If>
				<If condition={studentNote.noteType === StudentNoteNoteType.Injury && studentNote.studentInjuryNote !== null}>
					<InjuryDetails studentNote={studentNote} />
				</If>
				<If
					condition={
						studentNote.noteType === StudentNoteNoteType.Medication && studentNote.studentMedicationNote !== null
					}
				>
					<MedicationDetails studentNote={studentNote} />
				</If>
				<If
					condition={
						studentNote.noteType === StudentNoteNoteType.SocialEmotional &&
						studentNote.studentSocialEmotionalNote !== null
					}
				>
					<SocialEmotionalDetails studentNote={studentNote} />
				</If>
			</Conditional>

			<AttachmentsView studentNote={studentNote} />

			<Rule thickness="2px" color="var(--neutral-colors--stroke)" />

			<If condition={studentNote.requiresResolution}>
				<ResolutionStatus studentNote={studentNote} />

				<Rule thickness="2px" color="var(--neutral-colors--stroke)" />
			</If>

			<SharingView studentNoteID={studentNote.id} />

			<Rule thickness="2px" color="var(--neutral-colors--stroke)" />

			<Section>
				<Section.Header>
					<Heading.H4 noMargin>Discussion</Heading.H4>
				</Section.Header>
				<Section.Body>
					<SocialCommentComponents.CommentsView studentNoteID={studentNote.id} comments={studentNote.socialComments} />
				</Section.Body>
			</Section>
		</Column>
	);
}

const resolutionFormStateArgs = {
	editMode: EditMode.WriteOnly,
};

type ResolutionStatusProps = {
	studentNote: DetailDisplayStudentNote;
};

function ResolutionStatus({ studentNote }: ResolutionStatusProps) {
	const { isModalOpen, openModal, closeModal } = Modal.useToggle();

	if (!studentNote.requiresResolution) {
		return null;
	}

	return (
		<React.Fragment>
			<Column justify="spaced-start">
				<Column justify="spaced-start" verticalSpacing="0.125rem">
					<span className={styles["resolution-status__label"]}>Status</span>
					<Row justify="spaced-start" align="center">
						<span
							className={
								styles["resolution-status__status"] +
								" " +
								(studentNote.isResolved
									? styles["resolution-status__status--resolved"]
									: styles["resolution-status__status--unresolved"])
							}
						>
							{studentNote.isResolved ? "Resolved" : "Unresolved"}
						</span>

						<If condition={!studentNote.isResolved}>
							<StudentNoteUpdatePermissionRequired
								isAuthor={studentNote.author?.isCurrent ?? false}
								noteType={studentNote.noteType}
							>
								<Button variant="link" size="small" onClick={openModal}>
									Mark as Resolved
								</Button>
							</StudentNoteUpdatePermissionRequired>
						</If>
						{!!studentNote.resolvedAt && (
							<If condition={studentNote.isResolved}>
								<span className={styles["resolution-status__timestamp"]}>
									on <Timestamp value={studentNote.resolvedAt} precision="minute" />
								</span>
							</If>
						)}
					</Row>
				</Column>

				<If condition={studentNote.isResolved && studentNote.resolutionDescription !== ""}>
					<LabeledItem label="Resolution">
						<FormattedText>{studentNote.resolutionDescription}</FormattedText>
					</LabeledItem>
				</If>
			</Column>

			<Modal.If condition={isModalOpen}>
				<Modal onClose={closeModal} style={{ width: "30rem" }}>
					<Modal.Header>
						<Heading.H2 noMargin>Resolution</Heading.H2>
					</Modal.Header>
					<Modal.Body>
						<StudentNoteResolutionForm.ControlledDetail
							studentNote={studentNote}
							formStateArgs={resolutionFormStateArgs}
							noChangesDialog
							onSuccess={closeModal}
						/>
					</Modal.Body>
				</Modal>
			</Modal.If>
		</React.Fragment>
	);
}

type MutationMenuProps = {
	studentNote: DetailDisplayStudentNote;
};

function MutationMenu({ studentNote }: MutationMenuProps) {
	// const { isModalOpen: isEditModalOpen, openModal: openEditModal, closeModal: closeEditModal } = Modal.useToggle();

	return (
		<React.Fragment>
			<TooltipButton side="bottom">
				{() => (
					<Tooltip isInteractable>
						<Tooltip.Body>
							<Column justify="spaced-start" align="center" verticalSpacing="0.25rem">
								<StudentNoteUpdatePermissionRequired
									isAuthor={studentNote.author?.isCurrent ?? false}
									noteType={studentNote.noteType}
								>
									<ToggleIsPinnedActionButton studentNote={studentNote} />

									{/* <Button variant="link" size="small" onClick={openEditModal} style={{ width: "100%" }}>
										<Row justify="spaced-center" align="center" horizontalSpacing="0.25rem">
											<Icon.PencilSimple size={14} /> Edit
										</Row>
									</Button> */}
								</StudentNoteUpdatePermissionRequired>

								<StudentNoteDeletePermissionRequired
									isAuthor={studentNote.author?.isCurrent ?? false}
									noteType={studentNote.noteType}
								>
									<StudentNoteDeleteButton
										instanceID={studentNote.id}
										size="small"
										style={{ width: "100%" }}
										buttonStyle={{ width: "100%" }}
									>
										<Row justify="spaced-center" align="center" horizontalSpacing="0.25rem">
											<Icon.Trash size={14} /> Delete
										</Row>
									</StudentNoteDeleteButton>
								</StudentNoteDeletePermissionRequired>
							</Column>
						</Tooltip.Body>
					</Tooltip>
				)}
				{({ onClick }) => (
					<Button variant="link" size="small" onClick={onClick} className="button--icon">
						<Icon.DotsThreeVertical size={16} className="block" />
					</Button>
				)}
			</TooltipButton>
		</React.Fragment>
	);
}

type ToggleIsPinnedActionButtonProps = {
	studentNote: Pick<DetailDisplayStudentNote, "id" | "isPinned">;
};

function ToggleIsPinnedActionButton({ studentNote }: ToggleIsPinnedActionButtonProps) {
	const input = React.useMemo<StudentNoteUpdateInput>(
		() => ({
			isPinned: !studentNote.isPinned,
		}),
		[studentNote],
	);

	const initialFormValues = React.useMemo<StudentNoteFormValues.DetailSubset<"isPinned">>(
		() => ({
			isPinned: studentNote.isPinned,
		}),
		[studentNote],
	);

	return (
		<StudentNoteUpdateButton<"isPinned">
			instanceID={studentNote.id}
			input={input}
			initialFormValues={initialFormValues}
			variant="link"
			size="small"
			style={{ width: "100%" }}
		>
			<Row justify="spaced-center" align="center" horizontalSpacing="0.25rem">
				<Conditional>
					<If condition={studentNote.isPinned}>
						<Icon.PushPinSlash size={14} /> Unpin
					</If>
					<Else>
						<Icon.PushPin size={14} /> Pin
					</Else>
				</Conditional>
			</Row>
		</StudentNoteUpdateButton>
	);
}

type NoteTypeDetailsProps = {
	studentNote: DetailDisplayStudentNote;
};

function AcademicDetails({ studentNote }: NoteTypeDetailsProps) {
	if (!studentNote.studentAcademicNote) {
		return null;
	}

	return (
		<Column justify="spaced-start">
			<LabeledItem label={StudentAcademicNoteFormat.fieldSetLabels.issueNature}>
				<Conditional>
					<If
						condition={
							studentNote.studentAcademicNote.isIssueNatureLearningDifficulty ||
							studentNote.studentAcademicNote.isIssueNatureAttentionIssue ||
							studentNote.studentAcademicNote.isIssueNatureFocusIssue ||
							studentNote.studentAcademicNote.isIssueNatureEmotionalOrVulnerableIssue ||
							studentNote.studentAcademicNote.isIssueNatureOther
						}
					>
						<List>
							<If condition={studentNote.studentAcademicNote.isIssueNatureLearningDifficulty}>
								<List.Item>{StudentAcademicNoteFormat.fieldLabels.isIssueNatureLearningDifficulty}</List.Item>
							</If>

							<If condition={studentNote.studentAcademicNote.isIssueNatureAttentionIssue}>
								<List.Item>{StudentAcademicNoteFormat.fieldLabels.isIssueNatureAttentionIssue}</List.Item>
							</If>

							<If condition={studentNote.studentAcademicNote.isIssueNatureFocusIssue}>
								<List.Item>{StudentAcademicNoteFormat.fieldLabels.isIssueNatureFocusIssue}</List.Item>
							</If>

							<If condition={studentNote.studentAcademicNote.isIssueNatureEmotionalOrVulnerableIssue}>
								<List.Item>{StudentAcademicNoteFormat.fieldLabels.isIssueNatureEmotionalOrVulnerableIssue}</List.Item>
							</If>

							<If condition={studentNote.studentAcademicNote.isIssueNatureOther}>
								<List.Item>
									{StudentAcademicNoteFormat.fieldLabels.isIssueNatureOther}
									<If condition={!!studentNote.studentAcademicNote.issueNatureOther}>
										: <FormattedText>{studentNote.studentAcademicNote.issueNatureOther}</FormattedText>
									</If>
								</List.Item>
							</If>
						</List>
					</If>
					<Else>Not specified</Else>
				</Conditional>
			</LabeledItem>

			<LabeledItem label={StudentAcademicNoteFormat.fieldSetLabels.solutionRec}>
				<Conditional>
					<If
						condition={
							studentNote.studentAcademicNote.isSolutionRecDifferentiatedLearning ||
							studentNote.studentAcademicNote.isSolutionRecIndividualLearningPlan ||
							studentNote.studentAcademicNote.isSolutionRecTutoring ||
							studentNote.studentAcademicNote.isSolutionRecProfessionalSupport ||
							studentNote.studentAcademicNote.isSolutionRecInClassSupport ||
							studentNote.studentAcademicNote.isSolutionRecOther
						}
					>
						<List>
							<If condition={studentNote.studentAcademicNote.isSolutionRecDifferentiatedLearning}>
								<List.Item>{StudentAcademicNoteFormat.fieldLabels.isSolutionRecDifferentiatedLearning}</List.Item>
							</If>

							<If condition={studentNote.studentAcademicNote.isSolutionRecIndividualLearningPlan}>
								<List.Item>{StudentAcademicNoteFormat.fieldLabels.isSolutionRecIndividualLearningPlan}</List.Item>
							</If>

							<If condition={studentNote.studentAcademicNote.isSolutionRecTutoring}>
								<List.Item>{StudentAcademicNoteFormat.fieldLabels.isSolutionRecTutoring}</List.Item>
							</If>

							<If condition={studentNote.studentAcademicNote.isSolutionRecProfessionalSupport}>
								<List.Item>{StudentAcademicNoteFormat.fieldLabels.isSolutionRecProfessionalSupport}</List.Item>
							</If>

							<If condition={studentNote.studentAcademicNote.isSolutionRecInClassSupport}>
								<List.Item>{StudentAcademicNoteFormat.fieldLabels.isSolutionRecInClassSupport}</List.Item>
							</If>

							<If condition={studentNote.studentAcademicNote.isSolutionRecOther}>
								<List.Item>
									{StudentAcademicNoteFormat.fieldLabels.isSolutionRecOther}
									<If condition={!!studentNote.studentAcademicNote.solutionRecOther}>
										: <FormattedText>{studentNote.studentAcademicNote.solutionRecOther}</FormattedText>
									</If>
								</List.Item>
							</If>
						</List>
					</If>
					<Else>Not specified</Else>
				</Conditional>
			</LabeledItem>
		</Column>
	);
}

function AttendanceDetails({ studentNote }: NoteTypeDetailsProps) {
	if (!studentNote.studentAttendanceNote) {
		return null;
	}

	return (
		<Column justify="spaced-start">
			<LabeledItem label="Dates">
				{StudentAttendanceNoteFormat.dateRange(studentNote.studentAttendanceNote)}
			</LabeledItem>
			{!!studentNote.studentAttendanceNote.attendanceStatus && (
				<LabeledItem label="Attendance Status">
					<Row justify="spaced-start" align="center" horizontalSpacing="0.5rem">
						<AttendanceStatusBadge
							attendanceStatus={studentNote.studentAttendanceNote.attendanceStatus}
							style={{ color: "var(--neutral-colors--primary)", width: "fit-content" }}
						/>
						{AttendanceStatusFormat.name(studentNote.studentAttendanceNote.attendanceStatus)}
					</Row>
				</LabeledItem>
			)}
		</Column>
	);
}

function BehaviorDetails({ studentNote }: NoteTypeDetailsProps) {
	if (!studentNote.studentBehaviorNote) {
		return null;
	}

	return (
		<Column justify="spaced-start">
			<LabeledItem label={StudentBehaviorNoteFormat.fieldLabels.incidentTime}>
				{StudentBehaviorNoteFormat.Fields.incidentTime(studentNote.studentBehaviorNote.incidentTime)}
			</LabeledItem>

			<LabeledItem label={StudentBehaviorNoteFormat.fieldLabels.incidentLocation}>
				{StudentBehaviorNoteFormat.Fields.incidentLocation(studentNote.studentBehaviorNote.incidentLocation)}
			</LabeledItem>

			<LabeledItem label={StudentBehaviorNoteFormat.fieldSetLabels.incidentType}>
				<Conditional>
					<If
						condition={
							studentNote.studentBehaviorNote.isIncidentPhysicalContactAggression ||
							studentNote.studentBehaviorNote.isIncidentFightingPhysicalAggression ||
							studentNote.studentBehaviorNote.isIncidentVerbalAbuseDisrespectingStudent ||
							studentNote.studentBehaviorNote.isIncidentDisrepectingTeacher ||
							studentNote.studentBehaviorNote.isIncidentDisruption ||
							studentNote.studentBehaviorNote.isIncidentDefianceOrNonCompliance ||
							studentNote.studentBehaviorNote.isIncidentOther
						}
					>
						<List>
							<If condition={studentNote.studentBehaviorNote.isIncidentPhysicalContactAggression}>
								<List.Item>{StudentBehaviorNoteFormat.fieldLabels.isIncidentPhysicalContactAggression}</List.Item>
							</If>

							<If condition={studentNote.studentBehaviorNote.isIncidentFightingPhysicalAggression}>
								<List.Item>{StudentBehaviorNoteFormat.fieldLabels.isIncidentFightingPhysicalAggression}</List.Item>
							</If>

							<If condition={studentNote.studentBehaviorNote.isIncidentVerbalAbuseDisrespectingStudent}>
								<List.Item>{StudentBehaviorNoteFormat.fieldLabels.isIncidentVerbalAbuseDisrespectingStudent}</List.Item>
							</If>

							<If condition={studentNote.studentBehaviorNote.isIncidentDisrepectingTeacher}>
								<List.Item>{StudentBehaviorNoteFormat.fieldLabels.isIncidentDisrepectingTeacher}</List.Item>
							</If>

							<If condition={studentNote.studentBehaviorNote.isIncidentDisruption}>
								<List.Item>{StudentBehaviorNoteFormat.fieldLabels.isIncidentDisruption}</List.Item>
							</If>

							<If condition={studentNote.studentBehaviorNote.isIncidentDefianceOrNonCompliance}>
								<List.Item>{StudentBehaviorNoteFormat.fieldLabels.isIncidentDefianceOrNonCompliance}</List.Item>
							</If>

							<If condition={studentNote.studentBehaviorNote.isIncidentOther}>
								<List.Item>
									{StudentBehaviorNoteFormat.fieldLabels.isIncidentOther}
									<If condition={!!studentNote.studentBehaviorNote.incidentOther}>
										: <FormattedText>{studentNote.studentBehaviorNote.incidentOther}</FormattedText>
									</If>
								</List.Item>
							</If>
						</List>
					</If>
					<Else>Not specified</Else>
				</Conditional>
			</LabeledItem>

			<LabeledItem label={StudentBehaviorNoteFormat.fieldSetLabels.motivation}>
				<Conditional>
					<If
						condition={
							studentNote.studentBehaviorNote.isMotivationAttentionSeeking ||
							studentNote.studentBehaviorNote.isMotivationReaction ||
							studentNote.studentBehaviorNote.isMotivationAnger ||
							studentNote.studentBehaviorNote.isMotivationFrustration ||
							studentNote.studentBehaviorNote.isMotivationHabit ||
							studentNote.studentBehaviorNote.isMotivationLackOfSelfContol ||
							studentNote.studentBehaviorNote.isMotivationOther
						}
					>
						<List>
							<If condition={studentNote.studentBehaviorNote.isMotivationAttentionSeeking}>
								<List.Item>{StudentBehaviorNoteFormat.fieldLabels.isMotivationAttentionSeeking}</List.Item>
							</If>

							<If condition={studentNote.studentBehaviorNote.isMotivationReaction}>
								<List.Item>{StudentBehaviorNoteFormat.fieldLabels.isMotivationReaction}</List.Item>
							</If>

							<If condition={studentNote.studentBehaviorNote.isMotivationAnger}>
								<List.Item>{StudentBehaviorNoteFormat.fieldLabels.isMotivationAnger}</List.Item>
							</If>

							<If condition={studentNote.studentBehaviorNote.isMotivationFrustration}>
								<List.Item>{StudentBehaviorNoteFormat.fieldLabels.isMotivationFrustration}</List.Item>
							</If>

							<If condition={studentNote.studentBehaviorNote.isMotivationHabit}>
								<List.Item>{StudentBehaviorNoteFormat.fieldLabels.isMotivationHabit}</List.Item>
							</If>

							<If condition={studentNote.studentBehaviorNote.isMotivationLackOfSelfContol}>
								<List.Item>{StudentBehaviorNoteFormat.fieldLabels.isMotivationLackOfSelfContol}</List.Item>
							</If>

							<If condition={studentNote.studentBehaviorNote.isMotivationOther}>
								<List.Item>
									{StudentBehaviorNoteFormat.fieldLabels.isMotivationOther}
									<If condition={!!studentNote.studentBehaviorNote.motivationOther}>
										: <FormattedText>{studentNote.studentBehaviorNote.motivationOther}</FormattedText>
									</If>
								</List.Item>
							</If>
						</List>
					</If>
					<Else>Not specified</Else>
				</Conditional>
			</LabeledItem>

			<LabeledItem label={StudentBehaviorNoteFormat.fieldSetLabels.consequenceRec}>
				<Conditional>
					<If
						condition={
							studentNote.studentBehaviorNote.isConsequenceRecDirectorIntervention ||
							studentNote.studentBehaviorNote.isConsequenceRecSenOrSelIntervention ||
							studentNote.studentBehaviorNote.isConsequenceRecStudentServiceTeamIntervention ||
							studentNote.studentBehaviorNote.isConsequenceRecParentContactFromReporter ||
							studentNote.studentBehaviorNote.isConsequenceRecParentContactFromSchoolDirector ||
							studentNote.studentBehaviorNote.isConsequenceRecOther
						}
					>
						<List>
							<If condition={studentNote.studentBehaviorNote.isConsequenceRecDirectorIntervention}>
								<List.Item>{StudentBehaviorNoteFormat.fieldLabels.isConsequenceRecDirectorIntervention}</List.Item>
							</If>

							<If condition={studentNote.studentBehaviorNote.isConsequenceRecSenOrSelIntervention}>
								<List.Item>{StudentBehaviorNoteFormat.fieldLabels.isConsequenceRecSenOrSelIntervention}</List.Item>
							</If>

							<If condition={studentNote.studentBehaviorNote.isConsequenceRecStudentServiceTeamIntervention}>
								<List.Item>
									{StudentBehaviorNoteFormat.fieldLabels.isConsequenceRecStudentServiceTeamIntervention}
								</List.Item>
							</If>

							<If condition={studentNote.studentBehaviorNote.isConsequenceRecParentContactFromReporter}>
								<List.Item>{StudentBehaviorNoteFormat.fieldLabels.isConsequenceRecParentContactFromReporter}</List.Item>
							</If>

							<If condition={studentNote.studentBehaviorNote.isConsequenceRecParentContactFromSchoolDirector}>
								<List.Item>
									{StudentBehaviorNoteFormat.fieldLabels.isConsequenceRecParentContactFromSchoolDirector}
								</List.Item>
							</If>

							<If condition={studentNote.studentBehaviorNote.isConsequenceRecOther}>
								<List.Item>
									{StudentBehaviorNoteFormat.fieldLabels.isConsequenceRecOther}
									<If condition={!!studentNote.studentBehaviorNote.consequenceRecOther}>
										: <FormattedText>{studentNote.studentBehaviorNote.consequenceRecOther}</FormattedText>
									</If>
								</List.Item>
							</If>
						</List>
					</If>
					<Else>Not specified</Else>
				</Conditional>
			</LabeledItem>

			<If condition={!!studentNote.studentBehaviorNote.additionalComments}>
				<LabeledItem label={StudentBehaviorNoteFormat.fieldLabels.additionalComments}>
					<FormattedText>{studentNote.studentBehaviorNote.additionalComments}</FormattedText>
				</LabeledItem>
			</If>
		</Column>
	);
}

function HealthDetails({ studentNote }: NoteTypeDetailsProps) {
	return <LabeledItem label="Additional Details">{studentNote.studentHealthNote?.body}</LabeledItem>;
}

function IncidentDetails({ studentNote }: NoteTypeDetailsProps) {
	if (!studentNote.studentIncidentNote) {
		return null;
	}

	return (
		<Column justify="spaced-start">
			<LabeledItem label={StudentIncidentNoteFormat.fieldLabels.incidentTime}>
				{StudentIncidentNoteFormat.Fields.incidentTime(studentNote.studentIncidentNote.incidentTime)}
			</LabeledItem>
			<LabeledItem label={StudentIncidentNoteFormat.fieldLabels.incidentLocation}>
				{StudentIncidentNoteFormat.Fields.incidentLocation(studentNote.studentIncidentNote.incidentLocation)}
			</LabeledItem>
			<LabeledItem label={StudentIncidentNoteFormat.fieldLabels.actionsTaken}>
				<FormattedText>
					{StudentIncidentNoteFormat.Fields.actionsTaken(studentNote.studentIncidentNote.actionsTaken)}
				</FormattedText>
			</LabeledItem>
			<LabeledItem label={StudentIncidentNoteFormat.fieldLabels.suggestionsForFuture}>
				<FormattedText>
					{StudentIncidentNoteFormat.Fields.suggestionsForFuture(studentNote.studentIncidentNote.suggestionsForFuture)}
				</FormattedText>
			</LabeledItem>
		</Column>
	);
}

function InjuryDetails({ studentNote }: NoteTypeDetailsProps) {
	if (!studentNote.studentInjuryNote) {
		return null;
	}

	return (
		<Column justify="spaced-start">
			<LabeledItem label={StudentInjuryNoteFormat.fieldLabels.incidentTime}>
				{StudentInjuryNoteFormat.Fields.incidentTime(studentNote.studentInjuryNote.incidentTime)}
			</LabeledItem>
			<LabeledItem label={StudentInjuryNoteFormat.fieldLabels.incidentLocation}>
				{StudentInjuryNoteFormat.Fields.incidentLocation(studentNote.studentInjuryNote.incidentLocation)}
			</LabeledItem>
			<LabeledItem label={StudentInjuryNoteFormat.fieldLabels.wereOtherStudentsCauseOfInjury}>
				{StudentInjuryNoteFormat.Fields.wereOtherStudentsCauseOfInjury(
					studentNote.studentInjuryNote.wereOtherStudentsCauseOfInjury,
				)}
			</LabeledItem>
			<LabeledItem label={StudentInjuryNoteFormat.fieldLabels.causeOfInjury}>
				<FormattedText>
					{StudentInjuryNoteFormat.Fields.causeOfInjury(studentNote.studentInjuryNote.causeOfInjury)}
				</FormattedText>
			</LabeledItem>
			<LabeledItem label={StudentInjuryNoteFormat.fieldLabels.medicalAttentionProvided}>
				<FormattedText>
					{StudentInjuryNoteFormat.Fields.medicalAttentionProvided(
						studentNote.studentInjuryNote.medicalAttentionProvided,
					)}
				</FormattedText>
			</LabeledItem>
			<If condition={!!studentNote.studentInjuryNote.adultWhoProvidedMedicalAttention}>
				<LabeledItem label={StudentInjuryNoteFormat.fieldLabels.adultWhoProvidedMedicalAttention}>
					{StudentInjuryNoteFormat.Fields.adultWhoProvidedMedicalAttention(
						studentNote.studentInjuryNote.adultWhoProvidedMedicalAttention,
					)}
				</LabeledItem>
			</If>
			<LabeledItem label={StudentInjuryNoteFormat.fieldSetLabels.actionTaken}>
				<Conditional>
					<If
						condition={
							studentNote.studentInjuryNote.isActionTakenHomeroomTeachersNotified ||
							studentNote.studentInjuryNote.isActionTakenParentNotifiedByPortal ||
							studentNote.studentInjuryNote.isActionTakenParentNotifiedByPhone ||
							studentNote.studentInjuryNote.isActionTakenParentsRequestedToCollectStudent ||
							studentNote.studentInjuryNote.isActionTakenParentNotificationNotNecessary ||
							studentNote.studentInjuryNote.isActionTakenOther
						}
					>
						<List>
							<If condition={studentNote.studentInjuryNote.isActionTakenHomeroomTeachersNotified}>
								<List.Item>{StudentInjuryNoteFormat.fieldLabels.isActionTakenHomeroomTeachersNotified}</List.Item>
							</If>

							<If condition={studentNote.studentInjuryNote.isActionTakenParentNotifiedByPortal}>
								<List.Item>{StudentInjuryNoteFormat.fieldLabels.isActionTakenParentNotifiedByPortal}</List.Item>
							</If>

							<If condition={studentNote.studentInjuryNote.isActionTakenParentNotifiedByPhone}>
								<List.Item>{StudentInjuryNoteFormat.fieldLabels.isActionTakenParentNotifiedByPhone}</List.Item>
							</If>

							<If condition={studentNote.studentInjuryNote.isActionTakenParentsRequestedToCollectStudent}>
								<List.Item>
									{StudentInjuryNoteFormat.fieldLabels.isActionTakenParentsRequestedToCollectStudent}
								</List.Item>
							</If>

							<If condition={studentNote.studentInjuryNote.isActionTakenParentNotificationNotNecessary}>
								<List.Item>{StudentInjuryNoteFormat.fieldLabels.isActionTakenParentNotificationNotNecessary}</List.Item>
							</If>

							<If condition={studentNote.studentInjuryNote.isActionTakenOther}>
								<List.Item>
									{StudentInjuryNoteFormat.fieldLabels.isActionTakenOther}
									<If condition={!!studentNote.studentInjuryNote.actionTakenOther}>
										: <FormattedText>{studentNote.studentInjuryNote.actionTakenOther}</FormattedText>
									</If>
								</List.Item>
							</If>
						</List>
					</If>
					<Else>Not specified</Else>
				</Conditional>
			</LabeledItem>
		</Column>
	);
}

function MedicationDetails({ studentNote }: NoteTypeDetailsProps) {
	return <LabeledItem label="Additional Details">{studentNote.studentMedicationNote?.body}</LabeledItem>;
}

function SocialEmotionalDetails({ studentNote }: NoteTypeDetailsProps) {
	if (!studentNote.studentSocialEmotionalNote) {
		return null;
	}

	return (
		<Column justify="spaced-start">
			<LabeledItem label={StudentSocialEmotionalNoteFormat.fieldSetLabels.issueNature}>
				<Conditional>
					<If
						condition={
							studentNote.studentSocialEmotionalNote.isIssueNatureLearningDifficulty ||
							studentNote.studentSocialEmotionalNote.isIssueNatureAttentionIssue ||
							studentNote.studentSocialEmotionalNote.isIssueNatureFocusIssue ||
							studentNote.studentSocialEmotionalNote.isIssueNatureEmotionalOrVulnerableIssue ||
							studentNote.studentSocialEmotionalNote.isIssueNatureOther
						}
					>
						<List>
							<If condition={studentNote.studentSocialEmotionalNote.isIssueNatureLearningDifficulty}>
								<List.Item>{StudentSocialEmotionalNoteFormat.fieldLabels.isIssueNatureLearningDifficulty}</List.Item>
							</If>

							<If condition={studentNote.studentSocialEmotionalNote.isIssueNatureAttentionIssue}>
								<List.Item>{StudentSocialEmotionalNoteFormat.fieldLabels.isIssueNatureAttentionIssue}</List.Item>
							</If>

							<If condition={studentNote.studentSocialEmotionalNote.isIssueNatureFocusIssue}>
								<List.Item>{StudentSocialEmotionalNoteFormat.fieldLabels.isIssueNatureFocusIssue}</List.Item>
							</If>

							<If condition={studentNote.studentSocialEmotionalNote.isIssueNatureEmotionalOrVulnerableIssue}>
								<List.Item>
									{StudentSocialEmotionalNoteFormat.fieldLabels.isIssueNatureEmotionalOrVulnerableIssue}
								</List.Item>
							</If>

							<If condition={studentNote.studentSocialEmotionalNote.isIssueNatureOther}>
								<List.Item>
									{StudentSocialEmotionalNoteFormat.fieldLabels.isIssueNatureOther}
									<If condition={!!studentNote.studentSocialEmotionalNote.issueNatureOther}>
										: <FormattedText>{studentNote.studentSocialEmotionalNote.issueNatureOther}</FormattedText>
									</If>
								</List.Item>
							</If>
						</List>
					</If>
					<Else>Not specified</Else>
				</Conditional>
			</LabeledItem>

			<LabeledItem label={StudentSocialEmotionalNoteFormat.fieldSetLabels.solutionRec}>
				<Conditional>
					<If
						condition={
							studentNote.studentSocialEmotionalNote.isSolutionRecDifferentiatedLearning ||
							studentNote.studentSocialEmotionalNote.isSolutionRecIndividualLearningPlan ||
							studentNote.studentSocialEmotionalNote.isSolutionRecTutoring ||
							studentNote.studentSocialEmotionalNote.isSolutionRecProfessionalSupport ||
							studentNote.studentSocialEmotionalNote.isSolutionRecInClassSupport ||
							studentNote.studentSocialEmotionalNote.isSolutionRecOther
						}
					>
						<List>
							<If condition={studentNote.studentSocialEmotionalNote.isSolutionRecDifferentiatedLearning}>
								<List.Item>
									{StudentSocialEmotionalNoteFormat.fieldLabels.isSolutionRecDifferentiatedLearning}
								</List.Item>
							</If>

							<If condition={studentNote.studentSocialEmotionalNote.isSolutionRecIndividualLearningPlan}>
								<List.Item>
									{StudentSocialEmotionalNoteFormat.fieldLabels.isSolutionRecIndividualLearningPlan}
								</List.Item>
							</If>

							<If condition={studentNote.studentSocialEmotionalNote.isSolutionRecTutoring}>
								<List.Item>{StudentSocialEmotionalNoteFormat.fieldLabels.isSolutionRecTutoring}</List.Item>
							</If>

							<If condition={studentNote.studentSocialEmotionalNote.isSolutionRecProfessionalSupport}>
								<List.Item>{StudentSocialEmotionalNoteFormat.fieldLabels.isSolutionRecProfessionalSupport}</List.Item>
							</If>

							<If condition={studentNote.studentSocialEmotionalNote.isSolutionRecInClassSupport}>
								<List.Item>{StudentSocialEmotionalNoteFormat.fieldLabels.isSolutionRecInClassSupport}</List.Item>
							</If>

							<If condition={studentNote.studentSocialEmotionalNote.isSolutionRecOther}>
								<List.Item>
									{StudentSocialEmotionalNoteFormat.fieldLabels.isSolutionRecOther}
									<If condition={!!studentNote.studentSocialEmotionalNote.solutionRecOther}>
										: <FormattedText>{studentNote.studentSocialEmotionalNote.solutionRecOther}</FormattedText>
									</If>
								</List.Item>
							</If>
						</List>
					</If>
					<Else>Not specified</Else>
				</Conditional>
			</LabeledItem>
		</Column>
	);
}

type AttachmentsViewProps = {
	studentNote: DetailDisplayStudentNote;
};

function AttachmentsView({ studentNote }: AttachmentsViewProps) {
	const { visualAttachments, fileAttachments, linkAttachments } = React.useMemo(
		() => MediaAttachmentUtilities.splitAndExtractMediaAttachments(studentNote?.mediaAttachments, studentNote?.body),
		[studentNote],
	);

	const { isMediaAttachmentModalOpen, onClickMediaAttachment, closeMediaAttachmentModal, mediaAttachmentModalIndex } =
		MediaAttachmentComponents.MediaAttachmentModal.useModal();

	if (visualAttachments.length === 0 && fileAttachments.length === 0 && linkAttachments.length === 0) {
		return null;
	}

	return (
		<React.Fragment>
			<Rule thickness="2px" color="var(--neutral-colors--stroke)" />

			<Section>
				<Section.Header>
					<Heading.H4 noMargin>Attachments</Heading.H4>
				</Section.Header>
				<Section.Body>
					<Column justify="spaced-start" verticalSpacing="1rem">
						<If condition={visualAttachments.length > 0}>
							<MediaAttachmentComponents.VisualAttachmentsRow
								attachments={visualAttachments}
								onClickAttachment={onClickMediaAttachment}
								size="medium"
							/>

							<Modal.If condition={isMediaAttachmentModalOpen}>
								<MediaAttachmentComponents.MediaAttachmentModal
									mediaAttachments={visualAttachments}
									initialIndex={mediaAttachmentModalIndex}
									backButtonText="Back to Note"
									onClose={closeMediaAttachmentModal}
								/>
							</Modal.If>
						</If>
						<If condition={fileAttachments.length > 0}>
							<MediaAttachmentComponents.FileAttachmentsRow attachments={fileAttachments} />
						</If>
						<If condition={linkAttachments.length > 0}>
							<MediaAttachmentComponents.LinkAttachmentsRow attachments={linkAttachments} />
						</If>
					</Column>
				</Section.Body>
			</Section>
		</React.Fragment>
	);
}

type LabeledItemProps = {
	label: string;
} & Partial<ClassNameProps & StyleProps & ChildrenProps>;

function LabeledItem({ label, className, style, children }: LabeledItemProps) {
	return (
		<Column
			justify="spaced-start"
			verticalSpacing="0.25rem"
			className={makeClassName(styles["detail-display__labeled-item"], className)}
			style={style}
		>
			<span className={styles["detail-display__labeled-item__label"]}>{label}</span>
			{children}
		</Column>
	);
}
